const handleResponse = async <T>(
  response: Response,
  onUnauthorized?: () => void,
): Promise<T> => {
  const responseData = (await response.json()) as T;

  if (!response.ok) {
    if (response.status === 401 && onUnauthorized) {
      onUnauthorized();
    }

    throw { ...responseData, status: response.status };
  }

  return responseData;
};

export type FetcherProps<B> = {
  token?: string;
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: B;
  headers?: HeadersInit;
  timeout?: number;
  retries?: number;
  onUnauthorized?: () => void;
};

const fetcher = async <T, B = unknown>({
  token,
  url,
  method,
  body,
  headers,
  timeout = 30 * 1000,
  retries = 3,
  onUnauthorized,
}: FetcherProps<B>): Promise<T> => {
  const baseURL = import.meta.env.VITE_API_URL as string;

  const headersConfig = {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : '',
    ...headers,
  };

  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(baseURL + url, {
      method,
      headers: headersConfig,
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });

    clearTimeout(id);

    return await handleResponse<T>(response, onUnauthorized);
  } catch (error) {
    clearTimeout(id);
    const errorObject = error as Response;

    if (retries > 0 && errorObject.status > 500) {
      const delay = Math.pow(2, retries) * 1000;

      return new Promise<T>((resolve, reject) => {
        setTimeout(() => {
          return fetcher({
            token,
            url,
            method,
            body,
            headers,
            timeout,
            retries: retries - 1,
            onUnauthorized,
          }).then(
            (value) => resolve(value as T),
            (error) => reject(error),
          );
        }, delay);
      });
    } else {
      throw error;
    }
  }
};

export default fetcher;
